import React from 'react'
import classNames from 'classnames'
import { Trans } from '@lingui/macro'
import { ChartType } from 'chart.js'
import { CustomMetricChart } from './CustomMetricChart'
import { ButtonOutline } from 'front-end/src/components'
import IconRefresh from '../../../../../components/common/icon/IconRefresh'
import IconGraph from '../../../../../components/common/icon/IconGraph'
import { last } from 'lodash'
import dayjs from 'shared/dayjs'

import './CustomMetricBox.less'

export function oneBeforeLast<T>(array: Array<T>) {
  if (array.length >= 2) {
    return array[array.length - 2]
  } else {
    return undefined
  }
}

export const calculateTrend = (chartData: Array<IChartDataEntry>) => {
  if (chartData.length >= 2) {
    const lastEntry = (last(chartData) as IChartDataEntry).value
    const oneBeforeLastEntry = (oneBeforeLast(chartData) as IChartDataEntry)
      .value

    if (lastEntry > oneBeforeLastEntry) {
      return TrendEnum.Up
    } else if (lastEntry < oneBeforeLastEntry) {
      return TrendEnum.Down
    } else {
      return null
    }
  } else {
    return null
  }
}

interface IChartDataEntry {
  id: number
  timestamp: string
  value: number
}

interface ICustomMetricBoxProps {
  from: string
  to: string
  editable: boolean
  chartData: Array<IChartDataEntry>
  target: number | null | undefined
  metricName: string | null | undefined
  showModal: () => void
}

export enum TrendEnum {
  Up = 'up',
  Down = 'down'
}

export const CustomMetricBox = ({
  from,
  to,
  chartData,
  target,
  editable,
  metricName,
  showModal
}: ICustomMetricBoxProps) => {
  const graphOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scaleBeginAtZero: true,
    tooltipYPadding: 20,
    tooltipXPadding: 20,
    tooltips: {
      // Disable tooltip for target line
      filter: function (tooltipItem) {
        return tooltipItem.datasetIndex === 0
      }
    },
    bezierCurve: false,
    pointDot: false,
    datasetStrokeWidth: 1,
    scales: {
      yAxes: [
        {
          ticks: {
            fontSize: 11,
            fontColor: '#B1BFC5'
          },
          gridLines: {
            display: true,
            zeroLineColor: '#B1BFC5',
            color: 'transparent',
            drawBorder: true
          }
        }
      ],
      xAxes: [
        {
          ticks: {
            fontSize: 11,
            fontColor: '#B1BFC5'
          },
          gridLines: {
            display: true,
            zeroLineColor: '#B1BFC5',
            color: 'transparent'
          }
        }
      ]
    }
  }
  const isEmpty = chartData.length === 0

  if (isEmpty) {
    chartData = [
      { id: 0, value: 0.5, timestamp: '2020-05-18T22:00:00.000Z' },
      { id: 1, value: 1, timestamp: '2020-05-19T22:00:00.000Z' },
      { id: 2, value: 2, timestamp: '2020-05-20T22:00:00.000Z' },
      { id: 4, value: 4, timestamp: '2020-05-22T22:00:00.000Z' }
    ]
    graphOptions.tooltips = {
      filter: function () {
        return false
      }
    }
    graphOptions.scales = {
      yAxes: [
        {
          ticks: {
            // @ts-expect-error
            display: false
          },
          gridLines: {
            display: true,
            zeroLineColor: '#B1BFC5',
            color: 'transparent',
            drawBorder: true
          }
        }
      ],
      xAxes: [
        {
          ticks: {
            // @ts-expect-error
            display: false
          },
          gridLines: {
            display: true,
            zeroLineColor: '#B1BFC5',
            color: 'transparent'
          }
        }
      ]
    }
  }

  const currentValue = !isEmpty ? last(chartData)?.value?.toString() : '0'

  const renderTrend = (trend: TrendEnum | null) => {
    if (!trend) {
      return null
    }

    if (trend === TrendEnum.Up) {
      return '▲'
    } else if (trend === TrendEnum.Down) {
      return '▼'
    }
  }
  const trend = calculateTrend(chartData)

  return (
    <div className="DataSummaryGrid__item">
      <div className="Box CustomMetricBox">
        <div className="CustomMetricBox__data">
          <div className="CustomMetricBox__title">
            <IconGraph />
            <div>
              <div className="CustomMetricBox__title__text">
                {metricName || 'Custom Metric'}
              </div>
              <div className="CustomMetricBox__title__numbers">
                <div className="CustomMetricBox__title__numbers__current">
                  <div className="CustomMetricBox__title__numbers__current__number">
                    {currentValue}
                  </div>
                  <div
                    className={classNames(
                      'CustomMetricBox__title__numbers__current__trend',
                      `--${trend}`,
                      { '--grey': isEmpty }
                    )}
                  >
                    {renderTrend(trend)}
                  </div>
                </div>
                <div className="CustomMetricBox__title__numbers__target">
                  <small>
                    <Trans>Target</Trans>
                  </small>
                  <div>{target || 0}</div>
                </div>
              </div>
            </div>
          </div>
          {editable && (
            <div className="CustomMetricBox__action">
              <ButtonOutline onClick={showModal} leftIcon={<IconRefresh />}>
                {isEmpty ? (
                  <Trans>Upload data</Trans>
                ) : (
                  <Trans>Update data</Trans>
                )}
              </ButtonOutline>
            </div>
          )}
        </div>
        <div className="CustomMetricBox__chart">
          <CustomMetricChart
            width={400}
            height={180}
            config={{
              type: 'line' as ChartType,
              options: graphOptions
            }}
            from={dayjs(from).toDate()}
            to={dayjs(to).toDate()}
            chartData={chartData}
            target={target}
            color={isEmpty ? '#C8C8C8' : '#BD10E0'}
          />
        </div>
      </div>
    </div>
  )
}
